import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, Loading, formatDate, formatCurrency, ouvrirDocument, BackButton, niveauMessage, couleurMessage, BadgeSignatureFormateur } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ── Constantes ────────────────────────────────────────────────────────────────

const TYPES_DOCUMENT = [
  'KBIS / Certificat d\'immatriculation',
  'Attestation fiscale (URSSAF)',
  'Attestation sociale (URSSAF)',
  'Attestation d\'assurance RC Pro',
  'Attestation d\'assurance décennale',
  'Carte professionnelle / Habilitation',
  'Curriculum Vitae',
  'Diplôme / Certification',
  'Contrat / Convention cadre',
  'Autre',
];

const UNITES_TARIF = ['Jour', 'Demi-journée', 'Heure', 'Forfait'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function docStatut(dateFinValidite) {
  if (!dateFinValidite) return null;
  const fin = new Date(dateFinValidite);
  const today = new Date();
  const diffDays = Math.round((fin - today) / 86400000);
  if (diffDays < 0) return { label: 'Expiré', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
  if (diffDays <= 60) return { label: `Expire dans ${diffDays}j`, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
  return { label: 'Valide', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' };
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / 1048576).toFixed(1)} Mo`;
}

// ── Styles partagés ───────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
  color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
};

const sectionTitle = {
  fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
  color: 'var(--text-tertiary)', letterSpacing: '0.07em',
  margin: '1.2rem 0 0.6rem',
};

// ── Modal modifier formateur ──────────────────────────────────────────────────

function ModalModifier({ formateur, onSave, onClose }) {
  const [form, setForm] = useState({
    nom_formateur: formateur.nom_formateur || '',
    societe: formateur.societe || '',
    email: formateur.email || '',
    tel: formateur.tel || '',
    adresse1: formateur.adresse1 || '',
    adresse2: formateur.adresse2 || '',
    cp: formateur.cp || '',
    ville: formateur.ville || '',
    siret: formateur.siret || '',
    contrat_ref: formateur.contrat_ref || '',
    actif: formateur.actif ?? true,
    assujetti_tva: formateur.assujetti_tva ?? false,
    type_formateur: formateur.type_formateur || 'vacataire',
    organisme_id: formateur.organisme_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [organismes, setOrganismes] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    apiFetch('/organismes-formation').then(r => setOrganismes(r.data || [])).catch(() => {});
  }, []);

  function field(label, key, type = 'text') {
    return (
      <div style={{ marginBottom: '0.65rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{label}</label>
        <input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiFetch(`/formateurs/${formateur.formateur_id}`, { method: 'PUT', body: JSON.stringify(form) });
      onSave();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: '12px', padding: '2rem', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <h3 style={{ marginBottom: '1.2rem', color: '#111' }}>✏️ Modifier — {formateur.nom_formateur}</h3>

        {/* Type interne / vacataire / of */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem' }}>
          {[{ val: 'interne', label: '👤 Interne' }, { val: 'vacataire', label: '🏢 Vacataire' }, { val: 'of', label: '🏛 Organisme de Formation' }].map(t => (
            <button key={t.val} type="button" onClick={() => set('type_formateur', t.val)}
              style={{ flex: 1, padding: '0.55rem', borderRadius: '8px', border: '2px solid', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
                borderColor: form.type_formateur === t.val ? '#1a3668' : '#ccc',
                background: form.type_formateur === t.val ? 'rgba(26,54,104,0.08)' : '#f9f9f9',
                color: form.type_formateur === t.val ? '#1a3668' : '#666' }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid-2" style={{ gap: '0 0.8rem' }}>
          {field('Nom formateur *', 'nom_formateur')}
          {form.type_formateur !== 'of' && field('Société / Organisme', 'societe')}
        </div>
        {form.type_formateur === 'of' && (
          <div style={{ marginBottom: '0.65rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>
              Organisme de Formation *
            </label>
            <select
              value={form.organisme_id || ''}
              onChange={e => set('organisme_id', e.target.value ? Number(e.target.value) : null)}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">— Sélectionner un organisme —</option>
              {organismes.map(o => (
                <option key={o.id} value={o.id}>{o.nom}</option>
              ))}
            </select>
            {organismes.length === 0 && (
              <p style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '0.3rem' }}>
                Aucun OF enregistré — créez-en un dans Paramètres &gt; Organismes de Formation
              </p>
            )}
          </div>
        )}
        <div className="grid-2" style={{ gap: '0 0.8rem' }}>
          {field('Email', 'email', 'email')}
          {field('Téléphone', 'tel')}
        </div>
        {field('Adresse', 'adresse1')}
        {field('Complément d\'adresse', 'adresse2')}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '0 0.8rem' }}>
          {field('CP', 'cp')}
          {field('Ville', 'ville')}
        </div>
        {form.type_formateur !== 'interne' && field('Référence contrat', 'contrat_ref')}
        {form.type_formateur !== 'interne' && field('SIRET', 'siret')}

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '0.6rem', color: '#333' }}>
          <input type="checkbox" checked={form.assujetti_tva} onChange={e => set('assujetti_tva', e.target.checked)} />
          Assujetti à la TVA (20 %)
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '1rem', color: '#333' }}>
          <input type="checkbox" checked={form.actif} onChange={e => set('actif', e.target.checked)} />
          Formateur actif
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Onglet 1 : Prestations & Formations ─────────────────────────────────────

function OngletPrestations({ formateur, formations, canEdit, onRefresh }) {
  const [editMode, setEditMode] = useState(false);
  const [editState, setEditState] = useState({});
  const [saving, setSaving] = useState(false);

  const liens = formateur.formation_liens || [];

  // Construire l'état d'édition initial depuis les liens existants
  function buildEditState() {
    const state = {};
    // Initialiser toutes les formations CEJ comme non cochées
    formations.forEach(fo => {
      state[fo.formation_id] = { checked: false, prix_unitaire_ht: '', unite_tarif: 'Jour' };
    });
    // Cocher et remplir celles qui ont un lien
    liens.forEach(l => {
      state[l.formation_id] = {
        checked: true,
        prix_unitaire_ht: l.prix_unitaire_ht != null ? String(l.prix_unitaire_ht) : '',
        unite_tarif: l.unite_tarif || 'Jour',
      };
    });
    return state;
  }

  function openEdit() {
    setEditState(buildEditState());
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setEditState({});
  }

  async function saveEdit() {
    setSaving(true);
    try {
      const formations_payload = Object.entries(editState)
        .filter(([, v]) => v.checked)
        .map(([formation_id, v]) => ({
          formation_id,
          prix_unitaire_ht: v.prix_unitaire_ht !== '' ? parseFloat(v.prix_unitaire_ht) : null,
          unite_tarif: v.unite_tarif,
        }));
      await apiFetch(`/formateurs/${formateur.formateur_id}/formation-liens/bulk`, {
        method: 'POST',
        body: JSON.stringify({ formations: formations_payload }),
      });
      setEditMode(false);
      setEditState({});
      onRefresh();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setSaving(false); }
  }

  function setField(formationId, key, value) {
    setEditState(s => ({ ...s, [formationId]: { ...s[formationId], [key]: value } }));
  }

  // ── Mode vue ──
  if (!editMode) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <p style={{ ...sectionTitle, margin: 0 }}>Formations dispensées</p>
          {canEdit && (
            <button className="btn-secondary" onClick={openEdit}
              style={{ fontSize: '0.82rem', padding: '0.3rem 0.9rem' }}>
              ✏️ Modifier
            </button>
          )}
        </div>

        {liens.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', fontStyle: 'italic' }}>
            Aucune formation enregistrée pour ce formateur.
          </p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Formation</th>
                <th>Catégorie</th>
                <th style={{ textAlign: 'right' }}>Durée</th>
                <th style={{ textAlign: 'right' }}>Prix HT</th>
                <th>Unité</th>
              </tr>
            </thead>
            <tbody>
              {liens.map(l => (
                <tr key={l.lien_id}>
                  <td><strong>{l.intitule}</strong></td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{l.categorie || '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{l.duree_heures ? `${parseFloat(l.duree_heures)} h` : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {l.prix_unitaire_ht != null ? formatCurrency(l.prix_unitaire_ht) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td><Badge variant="badge-neutral">{l.unite_tarif || 'Jour'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ── Mode édition ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ ...sectionTitle, margin: 0 }}>Sélectionner les formations et renseigner les tarifs</p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={cancelEdit} disabled={saving}
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.9rem' }}>
            Annuler
          </button>
          <button className="btn-primary" onClick={saveEdit} disabled={saving}
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.9rem' }}>
            {saving ? 'Enregistrement…' : '✓ Enregistrer'}
          </button>
        </div>
      </div>

      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 36 }}></th>
            <th>Formation</th>
            <th>Catégorie</th>
            <th style={{ textAlign: 'right', width: 70 }}>Durée</th>
            <th style={{ width: 130 }}>Prix HT (€)</th>
            <th style={{ width: 120 }}>Unité</th>
          </tr>
        </thead>
        <tbody>
          {formations.map(fo => {
            const s = editState[fo.formation_id] || { checked: false, prix_unitaire_ht: '', unite_tarif: 'Jour' };
            return (
              <tr key={fo.formation_id}
                style={{ background: s.checked ? 'rgba(34,197,94,0.05)' : undefined, opacity: s.checked ? 1 : 0.55 }}>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={s.checked}
                    onChange={e => setField(fo.formation_id, 'checked', e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                </td>
                <td><strong style={{ fontSize: '0.88rem' }}>{fo.intitule}</strong></td>
                <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{fo.categorie || '—'}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fo.duree_heures ? `${parseFloat(fo.duree_heures)} h` : '—'}</td>
                <td>
                  <input type="number" min="0" step="0.01"
                    value={s.prix_unitaire_ht}
                    onChange={e => setField(fo.formation_id, 'prix_unitaire_ht', e.target.value)}
                    disabled={!s.checked}
                    placeholder="0"
                    style={{ ...inputStyle, padding: '0.25rem 0.4rem', textAlign: 'right', opacity: s.checked ? 1 : 0.4 }} />
                </td>
                <td>
                  <select value={s.unite_tarif}
                    onChange={e => setField(fo.formation_id, 'unite_tarif', e.target.value)}
                    disabled={!s.checked}
                    style={{ ...inputStyle, padding: '0.25rem 0.4rem', opacity: s.checked ? 1 : 0.4 }}>
                    {UNITES_TARIF.map(u => <option key={u}>{u}</option>)}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Onglet 2 : Documents administratifs ──────────────────────────────────────

function OngletDocuments({ formateur, canEdit, onRefresh }) {
  const inputFileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [typesDocument, setTypesDocument] = useState(TYPES_DOCUMENT);
  const [typesDuree, setTypesDuree] = useState({}); // { [nom]: duree_mois }
  const [addForm, setAddForm] = useState({ type_document: TYPES_DOCUMENT[0], date_fin_validite: '', notes: '' });
  const [customType, setCustomType] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [iaConfiance, setIaConfiance] = useState(null); // 'haute'|'moyenne'|'faible'|null
  const [iaNomAlert, setIaNomAlert] = useState(false); // true si le nom du bénéficiaire ne correspond pas
  const [previewUrl, setPreviewUrl] = useState(null); // object URL pour aperçu
  const [showPreview, setShowPreview] = useState(false);

  const docs = formateur.documents || [];

  // Charger les types depuis les paramètres
  useEffect(() => {
    const token = localStorage.getItem('gf_token');
    fetch(`${API_BASE}/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const raw = d.types_document_formateur;
        if (raw) {
          try {
            const arr = JSON.parse(raw);
            const names = arr.map(item => typeof item === 'string' ? item : item.nom);
            setTypesDocument(names);
            const dureeMap = {};
            arr.forEach(item => {
              if (typeof item === 'object' && item.nom && item.duree_mois) {
                dureeMap[item.nom] = parseInt(item.duree_mois);
              }
            });
            setTypesDuree(dureeMap);
          } catch {}
        }
      })
      .catch(() => {});
  }, []);

  // Grouper par type
  const byType = {};
  docs.forEach(d => {
    const t = d.type_document || 'Autre';
    if (!byType[t]) byType[t] = [];
    byType[t].push(d);
  });

  async function analyserDocument(file) {
    // Seuls PDF et images sont analysables par l'IA
    const analysable = file.type === 'application/pdf' || file.type.startsWith('image/');
    setPendingFile(file);
    setIaConfiance(null);
    setIaNomAlert(false);
    // Créer l'URL d'aperçu pour PDF et images
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const canPreview = file.type === 'application/pdf' || file.type.startsWith('image/');
    setPreviewUrl(canPreview ? URL.createObjectURL(file) : null);
    setShowPreview(false);
    if (!analysable) return;
    return; // Analyse IA désactivée (ANTHROPIC_API_KEY non configurée)
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('formateur_id', formateur.formateur_id);
      const token = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/ia/analyser-document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) return; // échec silencieux, l'utilisateur remplit manuellement
      const data = await res.json();
      if (data.non_analysable) return;
      // Calcul de date_fin_validite si non fournie par l'IA mais durée paramétrée + date_emission disponibles
      let dateFin = data.date_fin_validite || '';
      if (!dateFin && data.date_emission && typesDuree[data.type_document]) {
        try {
          const d = new Date(data.date_emission);
          d.setMonth(d.getMonth() + typesDuree[data.type_document]);
          dateFin = d.toISOString().slice(0, 10);
        } catch {}
      }
      setAddForm({
        type_document: data.type_document || TYPES_DOCUMENT[0],
        date_fin_validite: dateFin,
        notes: data.notes || '',
      });
      setIaConfiance(data.confiance || null);
      setIaNomAlert(data.nom_beneficiaire_ok === false);
    } catch {
      // échec silencieux
    } finally {
      setAnalysing(false);
    }
  }

  async function upload(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('type_document', addForm.type_document);
      if (addForm.date_fin_validite) fd.append('date_fin_validite', addForm.date_fin_validite);
      if (addForm.notes) fd.append('notes', addForm.notes);

      const token = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/formateurs/${formateur.formateur_id}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setAddForm({ type_document: TYPES_DOCUMENT[0], date_fin_validite: '', notes: '' });
      setPendingFile(null);
      setIaConfiance(null);
      setIaNomAlert(false);
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
      setShowPreview(false);
      onRefresh();
    } catch (e) { alert('Erreur upload : ' + e.message); }
    finally { setUploading(false); }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) analyserDocument(file);
  }

  async function saveEditDoc() {
    try {
      await apiFetch(`/formateurs/documents/${editDoc.id}`, { method: 'PUT', body: JSON.stringify(editDoc) });
      setEditDoc(null);
      onRefresh();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function deleteDoc(id) {
    if (!window.confirm('Supprimer ce document ?')) return;
    await apiFetch(`/formateurs/documents/${id}`, { method: 'DELETE' });
    onRefresh();
  }


  return (
    <div>
      {canEdit && (
        <>
          <p style={sectionTitle}>Déposer un nouveau document</p>

          {/* Paramètres upload */}
          <div className="grid-2" style={{ gap: '0 0.8rem', marginBottom: '0.7rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Type de document</label>
              <select value={showCustomInput ? '__custom__' : addForm.type_document}
                onChange={e => {
                  if (e.target.value === '__custom__') { setShowCustomInput(true); setCustomType(''); }
                  else {
                    const selected = e.target.value;
                    setShowCustomInput(false);
                    // Auto-calcul date_fin_validite si durée paramétrée et aucune date déjà saisie
                    let dateFin = addForm.date_fin_validite;
                    if (!dateFin && typesDuree[selected]) {
                      try {
                        const d = new Date();
                        d.setMonth(d.getMonth() + typesDuree[selected]);
                        dateFin = d.toISOString().slice(0, 10);
                      } catch {}
                    }
                    setAddForm({ ...addForm, type_document: selected, date_fin_validite: dateFin });
                  }
                }} style={inputStyle}>
                {typesDocument.map(t => <option key={t}>{t}</option>)}
                <option value="__custom__">+ Autre type…</option>
              </select>
              {showCustomInput && (
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem' }}>
                  <input value={customType} onChange={e => setCustomType(e.target.value)}
                    placeholder="Saisir un type…" style={{ ...inputStyle, flex: 1 }} autoFocus />
                  <button onClick={() => {
                    if (!customType.trim()) return;
                    const t = customType.trim();
                    setAddForm({ ...addForm, type_document: t });
                    setShowCustomInput(false);
                    setCustomType('');
                  }} style={{ padding: '0.35rem 0.7rem', borderRadius: '6px', border: 'none', background: '#E87722', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>OK</button>
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Date de fin de validité</label>
              <input type="date" value={addForm.date_fin_validite} onChange={e => setAddForm({ ...addForm, date_fin_validite: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Notes</label>
            <input value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Notes optionnelles..." style={inputStyle} />
          </div>

          {/* Carte de proposition IA + boutons de validation */}
          {pendingFile && !analysing && !uploading && (
            <div style={{
              border: `1px solid ${iaNomAlert ? 'rgba(239,68,68,0.4)' : iaConfiance ? 'rgba(232,119,34,0.4)' : 'var(--border)'}`,
              borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '0.8rem',
              background: iaNomAlert ? 'rgba(239,68,68,0.04)' : 'var(--bg-elevated)',
            }}>
              {/* En-tête : confiance IA */}
              {iaConfiance && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.6rem', fontSize: '0.8rem',
                  color: iaConfiance === 'haute' ? '#16a34a' : iaConfiance === 'moyenne' ? '#d97706' : '#dc2626' }}>
                  <span>🤖</span>
                  <span>Analyse IA — confiance <strong>{iaConfiance === 'haute' ? 'haute ✓' : iaConfiance === 'moyenne' ? 'moyenne ⚠' : 'faible ✕'}</strong></span>
                </div>
              )}

              {/* Alerte nom bénéficiaire */}
              {iaNomAlert && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', padding: '0.45rem 0.65rem',
                  borderRadius: '6px', marginBottom: '0.6rem', background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.3)', fontSize: '0.79rem', color: '#dc2626' }}>
                  <span style={{ flexShrink: 0 }}>⚠️</span>
                  <span>Le nom figurant sur ce document ne semble pas correspondre à ce formateur. Vérifiez qu'il s'agit bien du bon document.</span>
                </div>
              )}

              {/* Résumé des champs détectés */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem' }}>
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Type : </span>
                  <strong style={{ color: 'var(--text-primary)' }}>{addForm.type_document}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text-tertiary)' }}>Expire : </span>
                  <strong style={{ color: addForm.date_fin_validite ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                    {addForm.date_fin_validite ? new Date(addForm.date_fin_validite).toLocaleDateString('fr-FR') : '—'}
                  </strong>
                </div>
                {addForm.notes && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: 'var(--text-tertiary)' }}>Notes : </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{addForm.notes}</span>
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => upload(pendingFile)}
                  style={{ flex: 1, minWidth: 140, padding: '0.55rem 1rem', borderRadius: '8px',
                    background: 'var(--primary)', color: '#fff', border: 'none',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
                  ✅ Enregistrer
                </button>
                {previewUrl && (
                  <button onClick={() => setShowPreview(true)}
                    style={{ padding: '0.55rem 0.9rem', borderRadius: '8px',
                      background: 'var(--bg-surface)', color: 'var(--text-primary)',
                      border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>
                    👁 Aperçu
                  </button>
                )}
                <button onClick={() => { setPendingFile(null); setIaConfiance(null); setIaNomAlert(false); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } setShowPreview(false); }}
                  style={{ padding: '0.55rem 0.9rem', borderRadius: '8px',
                    background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                    border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem' }}>
                  ✕ Annuler
                </button>
              </div>
            </div>
          )}

          {/* Zone drag & drop */}
          <div
            onDragOver={e => { e.preventDefault(); if (!analysing && !uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => { if (!analysing && !uploading) inputFileRef.current.click(); }}
            style={{
              border: `2px dashed ${dragOver ? '#E87722' : pendingFile ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '10px',
              padding: '1.8rem 1rem',
              textAlign: 'center',
              cursor: (analysing || uploading) ? 'default' : 'pointer',
              background: dragOver ? 'rgba(232,119,34,0.05)' : pendingFile ? 'rgba(232,119,34,0.04)' : 'var(--bg-elevated)',
              color: 'var(--text-secondary)',
              transition: 'all 0.15s',
              marginBottom: pendingFile && !uploading ? '0.7rem' : '1.2rem',
              fontSize: '0.88rem',
            }}>
            <input ref={inputFileRef} type="file" style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) analyserDocument(e.target.files[0]); }} />
            {uploading
              ? <span>⏳ Envoi en cours...</span>
              : analysing
                ? <>
                    <div style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>🤖</div>
                    <div><strong>Analyse IA en cours...</strong></div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>Détection du type et de la date de validité</div>
                  </>
                : pendingFile
                  ? <>
                      <div style={{ fontSize: '1.4rem', marginBottom: '0.3rem' }}>📄</div>
                      <div><strong>{pendingFile.name}</strong></div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>Cliquez pour changer de fichier</div>
                    </>
                  : <>
                      <div style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>📁</div>
                      <div><strong>Cliquez ou déposez un fichier ici</strong></div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>PDF, images, Word, Excel... (max. 100 Mo) — remplissez le type et la date manuellement</div>
                    </>
            }
          </div>

        </>
      )}

      {/* Liste documents */}
      <p style={sectionTitle}>Documents déposés ({docs.length})</p>

      {docs.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucun document.</p>
      )}

      {Object.entries(byType).map(([type, typeDocs]) => (
        <div key={type} style={{ marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.4rem', paddingBottom: '0.2rem', borderBottom: '1px solid var(--border)' }}>
            {type}
          </p>
          {typeDocs.map(doc => {
            const statut = docStatut(doc.date_fin_validite);
            return (
              <div key={doc.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                padding: '0.55rem 0.7rem', borderRadius: '7px', marginBottom: '0.35rem',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              }}>
                {editDoc?.id === doc.id ? (
                  /* Édition inline */
                  <div style={{ flex: 1, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input list={`types-doc-${doc.id}`} value={editDoc.type_document}
                      onChange={e => setEditDoc({ ...editDoc, type_document: e.target.value })}
                      style={{ ...inputStyle, flex: '1 1 180px' }} />
                    <datalist id={`types-doc-${doc.id}`}>
                      {typesDocument.map(t => <option key={t} value={t} />)}
                    </datalist>
                    <input type="date" value={editDoc.date_fin_validite
                      ? String(editDoc.date_fin_validite).substring(0, 10) : ''}
                      onChange={e => setEditDoc({ ...editDoc, date_fin_validite: e.target.value })}
                      style={{ ...inputStyle, flex: '0 0 150px' }} />
                    <input value={editDoc.notes || ''} onChange={e => setEditDoc({ ...editDoc, notes: e.target.value })}
                      placeholder="Notes..." style={{ ...inputStyle, flex: '1 1 150px' }} />
                    <button className="btn-primary" onClick={saveEditDoc} style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>✓</button>
                    <button className="btn-secondary" onClick={() => setEditDoc(null)} style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>✕</button>
                  </div>
                ) : (
                  <>
                    {/* Infos document */}
                    <span style={{ fontSize: '1.1rem' }}>
                      {doc.nom_original?.endsWith('.pdf') ? '📄' : doc.nom_original?.match(/\.(jpe?g|png|gif)$/i) ? '🖼️' : '📎'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button onClick={() => ouvrirDocument({ titre: doc.nom_original, url: `/formateurs/documents/${doc.id}/download`, nomFichier: doc.nom_original })}
                        style={{ color: 'var(--accent-blue)', fontWeight: 600, fontSize: '0.85rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                        {doc.nom_original}
                      </button>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                        {fmtSize(doc.taille)} · Déposé le {formatDate(doc.uploaded_at)}
                        {doc.notes && <span> · {doc.notes}</span>}
                      </div>
                    </div>

                    {/* Statut validité */}
                    {statut && (
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                        borderRadius: '12px', background: statut.bg, color: statut.color,
                        whiteSpace: 'nowrap',
                      }}>
                        {statut.label}
                      </span>
                    )}
                    {doc.date_fin_validite && !statut && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                        Valide jusqu'au {formatDate(doc.date_fin_validite)}
                      </span>
                    )}
                    {!doc.date_fin_validite && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                        Sans date de validité
                      </span>
                    )}

                    {/* Actions */}
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '0.2rem' }}>
                        <button onClick={() => setEditDoc({ ...doc })} title="Modifier"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0.2rem 0.3rem', fontSize: '0.9rem' }}>✏️</button>
                        <button onClick={() => deleteDoc(doc.id)} title="Supprimer"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', padding: '0.2rem 0.3rem', fontSize: '0.9rem' }}>🗑</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Modal aperçu document */}
      {showPreview && previewUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', flexDirection: 'column' }}
          onClick={e => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
              👁 Aperçu — {pendingFile?.name}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => upload(pendingFile)}
                style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                ✅ Enregistrer
              </button>
              <button onClick={() => setShowPreview(false)}
                style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
                ✕ Fermer
              </button>
            </div>
          </div>
          {pendingFile?.type?.startsWith('image/')
            ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}>
                <img src={previewUrl} alt="aperçu" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} />
              </div>
            : <iframe src={previewUrl} title="aperçu document" style={{ flex: 1, border: 'none', background: '#fff' }} />
          }
        </div>
      )}
    </div>
  );
}

// ── Badge statut commande (réutilisable) ─────────────────────────────────────
function CommandeStatutBadge({ statut }) {
  const cfg = {
    'Brouillon': { color: 'var(--text-secondary)', bg: 'var(--bg-elevated)', label: '🔖 Brouillon' },
    'Envoyé':    { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  label: '📨 Envoyé' },
    'Validé':    { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',   label: '✓ Validé' },
    'Refusé':    { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: '✗ Refusé' },
  }[statut] || { color: 'var(--text-tertiary)', bg: 'var(--bg-elevated)', label: statut || '—' };
  return (
    <span style={{
      fontSize: '0.75rem', fontWeight: 700, padding: '0.18rem 0.55rem',
      borderRadius: '4px', color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
    }}>{cfg.label}</span>
  );
}

// ── Zone dépôt contrats de mission (documents physiques signés) ───────────────
function ContratsMissionZone({ formateurId }) {
  const token = localStorage.getItem('gf_token');
  const inputRef = React.useRef();
  const [docs, setDocs] = React.useState([]);
  const [docsArchives, setDocsArchives] = React.useState([]);
  const [showArchives, setShowArchives] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [showDrop, setShowDrop] = React.useState(false);
  const [showFiles, setShowFiles] = React.useState(false);

  const load = React.useCallback(() => {
    const base = `${API_BASE}/formateurs/${formateurId}/documents?type_document=Contrat+de+mission`;
    fetch(`${base}&archived=false`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocs(Array.isArray(d.data) ? d.data : [])).catch(() => {});
    fetch(`${base}&archived=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocsArchives(Array.isArray(d.data) ? d.data : [])).catch(() => {});
  }, [formateurId, token]);

  React.useEffect(() => { load(); }, [load]);

  async function upload(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('type_document', 'Contrat de mission');
      const r = await fetch(`${API_BASE}/formateurs/${formateurId}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).error);
      load();
    } catch (e) { alert('Erreur upload : ' + e.message); }
    finally { setUploading(false); }
  }

  async function archiver(docId, archived) {
    await fetch(`${API_BASE}/formateurs/documents/${docId}/archive`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ archived }),
    });
    load();
  }

  async function supprimer(docId) {
    if (!window.confirm('Supprimer définitivement ce fichier ?')) return;
    await fetch(`${API_BASE}/formateurs/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  }

  function ouvrirDoc(doc) {
    ouvrirDocument({ titre: doc.nom_original || 'Document', url: `/formateurs/documents/${doc.id}/download`, nomFichier: doc.nom_original });
  }

  function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
  }

  function DocRow({ doc, archived }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.7rem', borderRadius: '7px', background: archived ? 'var(--bg-card)' : 'var(--bg-elevated)', border: '1px solid var(--border)', marginBottom: '0.3rem', opacity: archived ? 0.7 : 1 }}>
        <span style={{ fontSize: '1rem', flexShrink: 0 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.nom_original}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
            {fmtSize(doc.taille)}{doc.taille ? ' · ' : ''}
            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : '—'}
            {archived && <span style={{ marginLeft: '0.4rem', color: '#f59e0b', fontWeight: 600 }}>Archivé</span>}
          </div>
        </div>
        <button onClick={() => ouvrirDoc(doc)} title="Ouvrir"
          style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>👁</button>
        {!archived ? (
          <button onClick={() => archiver(doc.id, true)} title="Archiver"
            style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(245,158,11,0.5)', background: 'transparent', color: '#f59e0b', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>🗄</button>
        ) : (
          <button onClick={() => archiver(doc.id, false)} title="Restaurer"
            style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(22,163,74,0.5)', background: 'transparent', color: '#16a34a', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>↩</button>
        )}
        <button onClick={() => supprimer(doc.id)} title="Supprimer définitivement"
          style={{ padding: '0.2rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>🗑</button>
      </div>
    );
  }

  const totalActifs = docs.length;

  return (
    <div style={{ marginTop: '1.5rem', padding: '0.85rem 1.1rem', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: (showDrop || showFiles) ? '0.7rem' : 0 }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)', flex: 1 }}>
          📂 Contrats de mission déposés
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)', userSelect: 'none' }}>
          <input type="checkbox" checked={showDrop} onChange={e => setShowDrop(e.target.checked)} style={{ cursor: 'pointer' }} />
          Déposer
        </label>
        <button onClick={() => setShowFiles(f => !f)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: '1px solid var(--border)', borderRadius: '20px', padding: '0.15rem 0.55rem', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {totalActifs > 0 && (
            <span style={{ background: '#E87722', color: '#fff', borderRadius: '10px', padding: '0 0.4rem', fontSize: '0.72rem', fontWeight: 700 }}>{totalActifs}</span>
          )}
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showFiles ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
        </button>
      </div>

      {/* Zone drag & drop */}
      {showDrop && (
        <div
          onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) upload(f); }}
          onClick={() => !uploading && inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? '#E87722' : 'rgba(232,119,34,0.5)'}`,
            borderRadius: '8px', padding: '1.2rem 1rem', textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            background: dragOver ? 'rgba(232,119,34,0.06)' : 'transparent',
            color: 'var(--text-secondary)', fontSize: '0.82rem', transition: 'all 0.15s',
            marginBottom: showFiles ? '0.75rem' : 0,
          }}>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files[0]; if (f) { upload(f); e.target.value = ''; } }} />
          {uploading
            ? '⏳ Envoi en cours…'
            : <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📁</div>
                <strong>Cliquez ou déposez un fichier</strong>
                <div style={{ fontSize: '0.75rem', marginTop: '0.2rem', color: 'var(--text-tertiary)' }}>PDF, Word, image — 20 Mo max</div>
              </>}
        </div>
      )}

      {/* Liste des documents actifs */}
      {showFiles && (
        <div>
          {docs.length === 0 && docsArchives.length === 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', fontStyle: 'italic', margin: 0 }}>Aucun contrat déposé.</p>
          )}
          {docs.map(doc => <DocRow key={doc.id} doc={doc} archived={false} />)}

          {/* Section archives */}
          {docsArchives.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <button onClick={() => setShowArchives(a => !a)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-tertiary)', padding: 0, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showArchives ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
                {docsArchives.length} contrat{docsArchives.length > 1 ? 's' : ''} archivé{docsArchives.length > 1 ? 's' : ''}
              </button>
              {showArchives && (
                <div style={{ marginTop: '0.4rem' }}>
                  {docsArchives.map(doc => <DocRow key={doc.id} doc={doc} archived={true} />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Onglet 3 : Contrats de missions ──────────────────────────────────────────

function OngletConventions({ formateur, onRefresh }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('gf_token');
  const [loadingContrat, setLoadingContrat]   = useState(false);
  const [loadingLien, setLoadingLien]         = useState(false);
  const [contratInfo, setContratInfo]         = useState(null);
  const [lienGenere, setLienGenere]           = useState('');
  const [copied, setCopied]                   = useState(false);
  const [showLienModal, setShowLienModal]         = useState(false);
  const [emailFormateur, setEmailFormateur]       = useState('');
  const [sendingEmail, setSendingEmail]           = useState(false);
  const [msgEmail, setMsgEmail]                   = useState('');
  // Modal lien commande
  const [modalCommande, setModalCommande]         = useState(null); // { commande_id, numero }
  const [emailCommande, setEmailCommande]         = useState('');
  const [loadingLienCmd, setLoadingLienCmd]       = useState(false);
  const [sendingEmailCmd, setSendingEmailCmd]     = useState(false);
  const [msgEmailCmd, setMsgEmailCmd]             = useState('');
  const [lienCommande, setLienCommande]           = useState('');

  // Sessions qui ont une commande générée
  const sessionsAvecCommande = (formateur.sessions || []).filter(s => s.commande_id);

  // Charger les infos du contrat (statut, lien, signature)
  useEffect(() => {
    fetch(`${API_BASE}/formateurs/${formateur.formateur_id}/contrat-mission/info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setContratInfo(d); })
      .catch(() => {});
  }, [formateur.formateur_id, token]);

  async function genererContrat() {
    setLoadingContrat(true);
    await ouvrirDocument({ titre: `Contrat de mission — ${formateur.nom_formateur || formateur.formateur_id}`, url: `/formateurs/${formateur.formateur_id}/contrat-mission` });
    setLoadingContrat(false);
  }

  function ouvrirModalLien() {
    setEmailFormateur(formateur.email || '');
    setMsgEmail('');
    setShowLienModal(true);
  }

  async function genererLien(envoyerEmail = false) {
    setLoadingLien(true);
    setMsgEmail('');
    try {
      const r = await fetch(`${API_BASE}/formateurs/${formateur.formateur_id}/contrat-mission/generer-lien`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur génération lien');
      // Construire l'URL avec le bon hostname (évite http://localhost dans l'email)
      const lienCree = `${window.location.origin}/contrat-formateur/${d.token}`;
      setLienGenere(lienCree);
      setContratInfo(prev => ({ ...prev, statut: 'Envoyé', lien_token: d.token }));

      if (envoyerEmail && emailFormateur.trim()) {
        setSendingEmail(true);
        try {
          const re = await fetch(`${API_BASE}/formateurs/${formateur.formateur_id}/contrat-mission/envoyer-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ email: emailFormateur.trim(), lien: lienCree }),
          });
          const de = await re.json();
          if (!re.ok) throw new Error(de.error);
          setMsgEmail('✓ Email envoyé à ' + emailFormateur.trim());
          setTimeout(() => setShowLienModal(false), 2000);
        } catch (e) { setMsgEmail('❌ ' + e.message); }
        finally { setSendingEmail(false); }
      } else {
        setShowLienModal(false);
      }
    } catch (e) { setMsgEmail('❌ ' + e.message); }
    finally { setLoadingLien(false); }
  }

  function copierLien() {
    const lien = lienGenere || (contratInfo?.lien_token
      ? `${window.location.origin}/contrat-formateur/${contratInfo.lien_token}`
      : '');
    if (!lien) return;
    navigator.clipboard.writeText(lien).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function changerStatut(commandeId, statut) {
    try {
      await fetch(`${API_BASE}/commandes-formateur/${commandeId}/statut`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statut }),
      });
      onRefresh();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  function ouvrirModalCommande(s) {
    setModalCommande({ commande_id: s.commande_id, numero: s.commande_numero, lien_token: s.commande_lien_token });
    setEmailCommande(formateur.email || '');
    setMsgEmailCmd('');
    setLienCommande(s.commande_lien_token ? `${window.location.origin}/commande-formateur/${s.commande_lien_token}` : '');
  }

  async function genererLienCommande(envoyerEmail = false) {
    if (!modalCommande) return;
    setLoadingLienCmd(true);
    setMsgEmailCmd('');
    try {
      const r = await fetch(`${API_BASE}/commandes-formateur/${modalCommande.commande_id}/generer-lien`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur génération lien');
      const lienCree = `${window.location.origin}/commande-formateur/${d.token}`;
      setLienCommande(lienCree);
      onRefresh();

      if (envoyerEmail && emailCommande.trim()) {
        setSendingEmailCmd(true);
        try {
          const re = await fetch(`${API_BASE}/commandes-formateur/${modalCommande.commande_id}/envoyer-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ email: emailCommande.trim(), lien: lienCree }),
          });
          const de = await re.json();
          if (!re.ok) throw new Error(de.error);
          setMsgEmailCmd('✓ Email envoyé à ' + emailCommande.trim());
          setTimeout(() => setModalCommande(null), 2000);
        } catch (e) { setMsgEmailCmd('❌ ' + e.message); }
        finally { setSendingEmailCmd(false); }
      } else {
        setModalCommande(null);
      }
    } catch (e) { setMsgEmailCmd('❌ ' + e.message); }
    finally { setLoadingLienCmd(false); }
  }

  async function renvoyerEmailContrat() {
    if (!emailFormateur.trim()) return;
    setSendingEmail(true); setMsgEmail('');
    try {
      const lien = lienAffiche;
      const re = await fetch(`${API_BASE}/formateurs/${formateur.formateur_id}/contrat-mission/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailFormateur.trim(), lien }),
      });
      const de = await re.json();
      if (!re.ok) throw new Error(de.error);
      setMsgEmail('✓ Email renvoyé à ' + emailFormateur.trim());
      setTimeout(() => setShowLienModal(false), 2000);
    } catch (e) { setMsgEmail('❌ ' + e.message); }
    finally { setSendingEmail(false); }
  }

  async function renvoyerEmailCommande() {
    if (!emailCommande.trim() || !modalCommande) return;
    setSendingEmailCmd(true); setMsgEmailCmd('');
    try {
      const re = await fetch(`${API_BASE}/commandes-formateur/${modalCommande.commande_id}/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailCommande.trim(), lien: lienCommande }),
      });
      const de = await re.json();
      if (!re.ok) throw new Error(de.error);
      setMsgEmailCmd('✓ Email renvoyé à ' + emailCommande.trim());
      setTimeout(() => setModalCommande(null), 2000);
    } catch (e) { setMsgEmailCmd('❌ ' + e.message); }
    finally { setSendingEmailCmd(false); }
  }

  const lienAffiche = lienGenere || (contratInfo?.lien_token
    ? `${window.location.origin}/contrat-formateur/${contratInfo.lien_token}`
    : '');

  const statutColor = {
    'Brouillon': { color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
    'Envoyé':    { color: '#2563eb', bg: 'rgba(37,99,235,0.1)'   },
    'Signé':     { color: '#16a34a', bg: 'rgba(22,163,74,0.1)'   },
  };
  const sc = statutColor[contratInfo?.statut] || statutColor['Brouillon'];

  return (
    <div>

      {/* ── Contrat de mission cadre ── */}
      <p style={sectionTitle}>Contrat de mission cadre</p>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>
        Contrat cadre signé une fois lors du démarrage de la collaboration.
        Il définit les conditions générales de sous-traitance pour toutes les sessions.
      </p>

      <div style={{
        padding: '1rem', borderRadius: '8px',
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        marginBottom: '1.8rem',
      }}>
        {/* Ligne principale : titre + statut + boutons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>📄 Contrat de mission — {formateur.nom_formateur}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              Contrat cadre CEJ CONCEPT / Sous-traitant · Durée 1 an renouvelable
            </div>
          </div>

          {/* Badge statut */}
          {contratInfo?.statut && (
            <span style={{
              fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem',
              borderRadius: '20px', background: sc.bg, color: sc.color,
              whiteSpace: 'nowrap',
            }}>
              {contratInfo.statut === 'Signé' ? '✅' : contratInfo.statut === 'Envoyé' ? '📨' : '📝'} {contratInfo.statut}
            </span>
          )}

          <button className="btn-secondary" onClick={genererContrat} disabled={loadingContrat}
            style={{ fontSize: '0.82rem', padding: '0.4rem 1rem', whiteSpace: 'nowrap' }}>
            {loadingContrat ? '⏳...' : '📄 PDF'}
          </button>

          <button className="btn-primary" onClick={ouvrirModalLien} disabled={loadingLien}
            style={{ fontSize: '0.82rem', padding: '0.4rem 1rem', whiteSpace: 'nowrap' }}>
            {loadingLien ? '⏳...' : lienAffiche ? '✉ Renvoyer lien' : '🔗 Envoyer lien'}
          </button>
        </div>

        {/* Lien de signature */}
        {lienAffiche && (
          <div style={{
            marginTop: '0.8rem', padding: '0.6rem 0.8rem',
            background: 'var(--bg-card)', borderRadius: '6px',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '0.3rem', fontWeight: 600 }}>
              🔗 Lien de signature (valable 60 jours)
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                readOnly
                value={lienAffiche}
                style={{
                  flex: 1, fontSize: '0.78rem', padding: '0.35rem 0.6rem',
                  border: '1px solid var(--border)', borderRadius: '5px',
                  background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              />
              <button onClick={copierLien} style={{
                fontSize: '0.78rem', padding: '0.35rem 0.75rem',
                border: '1px solid var(--border)', borderRadius: '5px',
                background: copied ? '#16a34a' : 'var(--bg-elevated)',
                color: copied ? '#fff' : 'var(--text-primary)',
                cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
              }}>
                {copied ? '✅ Copié' : '📋 Copier'}
              </button>
            </div>
          </div>
        )}

        {/* Infos de signature */}
        {contratInfo?.statut === 'Signé' && contratInfo.signataire_nom && (
          <div style={{
            marginTop: '0.8rem', padding: '0.6rem 0.8rem',
            background: 'rgba(22,163,74,0.07)', borderRadius: '6px',
            border: '1px solid rgba(22,163,74,0.25)',
            fontSize: '0.82rem', color: '#16a34a',
          }}>
            ✅ Signé par <strong>{contratInfo.signataire_nom}</strong>
            {contratInfo.date_signature && (
              <span style={{ color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>
                · le {new Date(contratInfo.date_signature).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        )}
      </div>

      <ContratsMissionZone formateurId={formateur.formateur_id} />

      {/* ── Commandes par session ── */}
      <p style={{ ...sectionTitle, marginTop: '1.5rem' }}>Bons de commande</p>
      <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.8rem' }}>
        Les commandes sont générées depuis la fiche de chaque session (onglet <strong>Commande</strong>).
        Seules les sessions avec une commande apparaissent ici.
      </p>

      {sessionsAvecCommande.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', fontStyle: 'italic' }}>
          Aucune commande générée. Ouvrez une session et utilisez l'onglet "Commande".
        </p>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Commande</th>
              <th>Formation</th>
              <th>Dates</th>
              <th>Statut</th>
              <th style={{ width: '140px' }}></th>
            </tr>
          </thead>
          <tbody>
            {sessionsAvecCommande.map(s => (
              <tr key={s.session_id}>
                <td>
                  <strong style={{ color: 'var(--accent-blue)', cursor: 'pointer' }}
                    onClick={() => navigate(`/sessions/${s.session_id}`)}>
                    {s.commande_numero || s.session_id}
                  </strong>
                </td>
                <td style={{ fontSize: '0.85rem' }}>{s.intitule_formation}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                  {s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '—'}
                  {s.date_fin && s.date_fin !== s.date_debut
                    ? ` → ${new Date(s.date_fin).toLocaleDateString('fr-FR')}` : ''}
                </td>
                <td>
                  <select
                    value={s.commande_statut}
                    onChange={e => changerStatut(s.commande_id, e.target.value)}
                    style={{ fontSize: '0.78rem', padding: '0.15rem 0.4rem', borderRadius: '4px',
                      border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                      color: 'var(--text-primary)', cursor: 'pointer' }}>
                    {['Brouillon', 'Envoyé', 'Validé', 'Refusé'].map(st => (
                      <option key={st}>{st}</option>
                    ))}
                  </select>
                  <span style={{ marginLeft: '0.4rem' }}>
                    <CommandeStatutBadge statut={s.commande_statut} />
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/* Infos signature si validé */}
                  {s.commande_signataire_nom && (
                    <div style={{ fontSize: '0.7rem', color: '#16a34a', marginBottom: '3px' }}>
                      ✅ {s.commande_signataire_nom}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                    <button onClick={() => ouvrirModalCommande(s)}
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '4px',
                        border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                        color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                      🔗 Lien
                    </button>
                    <button
                      onClick={() => ouvrirDocument({ titre: `Bon de commande ${s.commande_numero || s.session_id}`, url: `/commandes-formateur/${s.commande_id}/pdf` })}
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '4px',
                        border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                        color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                      📄 PDF
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Modal génération lien commande ── */}
      {modalCommande && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '440px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              {lienCommande ? '✉ Renvoyer le lien de signature' : '🔗 Envoyer le lien de signature'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
              {lienCommande
                ? <>Le lien de signature existant sera renvoyé à <strong>{formateur.nom_formateur}</strong> pour la commande <strong>{modalCommande.numero}</strong>.</>
                : <>Un lien de signature sera généré et envoyé à <strong>{formateur.nom_formateur}</strong> pour la commande <strong>{modalCommande.numero}</strong>. Valable <strong>60 jours</strong>.</>}
            </p>

            {lienCommande && (
              <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: '0.3rem', fontWeight: 600 }}>🔗 Lien actif</div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={lienCommande}
                    style={{ flex: 1, fontSize: '0.75rem', padding: '0.3rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                  <button onClick={() => { navigator.clipboard.writeText(lienCommande); }}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    📋 Copier
                  </button>
                </div>
              </div>
            )}

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
              Email du formateur
            </label>
            <input type="email" value={emailCommande} onChange={e => setEmailCommande(e.target.value)}
              placeholder="formateur@exemple.fr" autoFocus
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1.5rem' }}
            />

            {msgEmailCmd && (
              <p style={{ fontSize: '0.85rem', marginBottom: '1rem', color: couleurMessage(niveauMessage(msgEmailCmd)), fontWeight: 600 }}>
                {msgEmailCmd}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn-secondary" onClick={() => setModalCommande(null)} disabled={loadingLienCmd || sendingEmailCmd}>
                {msgEmailCmd.startsWith('✓') ? 'Fermer' : 'Annuler'}
              </button>
              {!msgEmailCmd.startsWith('✓') && (
                <>
                  {lienCommande && (
                    <button onClick={() => genererLienCommande(true)} disabled={loadingLienCmd || sendingEmailCmd || !emailCommande.trim()}
                      style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                      ↺ Nouveau lien
                    </button>
                  )}
                  <button className="btn-primary" onClick={lienCommande ? renvoyerEmailCommande : () => genererLienCommande(true)}
                    disabled={loadingLienCmd || sendingEmailCmd || !emailCommande.trim()}>
                    {loadingLienCmd || sendingEmailCmd ? '⏳...' : lienCommande ? '✉ Renvoyer' : '✉ Envoyer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal génération lien de signature ── */}
      {showLienModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '440px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              {lienAffiche ? '✉ Renvoyer le lien de signature' : '🔗 Envoyer le lien de signature'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
              {lienAffiche
                ? <>Le lien de signature existant sera renvoyé à <strong>{formateur.nom_formateur}</strong>.</>
                : <>Un lien de signature sera généré et envoyé à <strong>{formateur.nom_formateur}</strong>. Il est valable <strong>60 jours</strong>.</>}
            </p>

            {lienAffiche && (
              <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: '0.3rem', fontWeight: 600 }}>🔗 Lien actif</div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input readOnly value={lienAffiche}
                    style={{ flex: 1, fontSize: '0.75rem', padding: '0.3rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                  <button onClick={copierLien}
                    style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', border: '1px solid var(--border)', borderRadius: '4px', background: copied ? '#16a34a' : 'var(--bg-elevated)', color: copied ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {copied ? '✅ Copié' : '📋 Copier'}
                  </button>
                </div>
              </div>
            )}

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
              Email du formateur
            </label>
            <input
              type="email"
              value={emailFormateur}
              onChange={e => setEmailFormateur(e.target.value)}
              placeholder="formateur@exemple.fr"
              autoFocus
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1.5rem' }}
            />

            {msgEmail && (
              <p style={{ fontSize: '0.85rem', marginBottom: '1rem', color: couleurMessage(niveauMessage(msgEmail)), fontWeight: 600 }}>
                {msgEmail}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn-secondary" onClick={() => setShowLienModal(false)} disabled={loadingLien || sendingEmail}>
                {msgEmail.startsWith('✓') ? 'Fermer' : 'Annuler'}
              </button>
              {!msgEmail.startsWith('✓') && (
                <>
                  {lienAffiche && (
                    <button onClick={() => genererLien(true)} disabled={loadingLien || sendingEmail || !emailFormateur.trim()}
                      style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                      ↺ Nouveau lien
                    </button>
                  )}
                  <button className="btn-primary" onClick={lienAffiche ? renvoyerEmailContrat : () => genererLien(true)}
                    disabled={loadingLien || sendingEmail || !emailFormateur.trim()}>
                    {loadingLien || sendingEmail ? '⏳...' : lienAffiche ? '✉ Renvoyer' : '✉ Envoyer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal demande de disponibilité ───────────────────────────────────────────
const API_BASE_DISPO = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

function ModalDisponibilite({ formateur, formations, onClose }) {
  const prenom = formateur.prenom || formateur.nom_formateur || 'toi';

  function defaultMessage() {
    return `Bonjour ${prenom},\n\nOn souhaite te proposer une intervention de formation et on aimerait connaître tes disponibilités.\n\nMerci de nous répondre directement à cet email.`;
  }

  const [mode, setMode] = React.useState('libre'); // 'programmee' | 'libre'
  const [sessionId, setSessionId] = React.useState('');
  const [formationId, setFormationId] = React.useState('');
  const [periode, setPeriode] = React.useState('');
  const [messageCorps, setMessageCorps] = React.useState(defaultMessage);
  const [email, setEmail] = React.useState(formateur.email || '');
  const [sending, setSending] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  // Sessions à venir du formateur
  const sessionsAvenir = (formateur.sessions || []).filter(s => {
    if (!s.date_debut) return false;
    return new Date(s.date_debut) >= new Date();
  }).sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));

  // Formations dispensées par ce formateur
  const formationsFormateur = formations.filter(f =>
    (formateur.formation_liens || []).some(fl => fl.formation_id === f.formation_id)
  );

  const inputS = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  async function envoyer() {
    if (!email.trim()) return setMsg('❌ Email requis');
    setSending(true); setMsg('');
    try {
      const token = localStorage.getItem('gf_token');
      const body = {
        email: email.trim(), mode,
        session_id: mode === 'programmee' ? sessionId : null,
        intitule_formation: mode === 'libre' ? (formations.find(f => f.formation_id === formationId)?.intitule || '') : null,
        periode: mode === 'libre' ? periode : null,
        message_corps: messageCorps,
      };
      const r = await fetch(`${API_BASE_DISPO}/formateurs/${formateur.formateur_id}/demande-disponibilite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur envoi');
      setMsg('✅ ' + d.message);
      setTimeout(onClose, 1800);
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSending(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>📧 Demande de disponibilité — {formateur.nom_formateur}</div>

        {/* Mode */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[{ val: 'programmee', label: '📅 Session programmée' }, { val: 'libre', label: '🗓 Période à définir' }].map(m => (
            <button key={m.val} onClick={() => setMode(m.val)}
              style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: '2px solid', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                borderColor: mode === m.val ? '#E87722' : 'var(--border)',
                background: mode === m.val ? 'rgba(232,119,34,0.08)' : 'var(--bg-elevated)',
                color: mode === m.val ? '#E87722' : 'var(--text-secondary)' }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Session programmée */}
        {mode === 'programmee' && (
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Session *</label>
            <select value={sessionId} onChange={e => setSessionId(e.target.value)} style={inputS}>
              <option value="">— Sélectionner une session —</option>
              {sessionsAvenir.length > 0 ? sessionsAvenir.map(s => (
                <option key={s.session_id} value={s.session_id}>
                  {s.session_id} — {s.intitule_formation} — {s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : ''}
                </option>
              )) : <option disabled>Aucune session à venir</option>}
            </select>
            {sessionsAvenir.length === 0 && (
              <div style={{ fontSize: '0.78rem', color: '#f59e0b', marginTop: 4 }}>Aucune session à venir — vous pouvez utiliser le mode "Période à définir"</div>
            )}
          </div>
        )}

        {/* Période libre */}
        {mode === 'libre' && (
          <>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Formation</label>
              <select value={formationId} onChange={e => setFormationId(e.target.value)} style={inputS}>
                <option value="">— Sélectionner une formation —</option>
                {formationsFormateur.map(f => <option key={f.formation_id} value={f.formation_id}>{f.intitule}</option>)}
                {formationsFormateur.length === 0 && <option disabled>Aucune formation renseignée</option>}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Dates / Semaine / Période souhaitée *</label>
              <input value={periode} onChange={e => setPeriode(e.target.value)} style={inputS}
                placeholder="Ex : semaine du 14 avril, courant mai, du 2 au 6 juin..." />
            </div>
          </>
        )}

        {/* Message */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Message <span style={{ fontWeight: 400 }}>(modifiable)</span></label>
            <button onClick={() => setMessageCorps(defaultMessage())}
              style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
              ↺ Réinitialiser
            </button>
          </div>
          <textarea value={messageCorps} onChange={e => setMessageCorps(e.target.value)} rows={5} style={{ ...inputS, resize: 'vertical', lineHeight: 1.5 }} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 3 }}>Les détails de la session/période sont ajoutés automatiquement.</div>
        </div>

        {/* Email */}
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Envoyer à *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputS} placeholder="email@formateur.fr" />
        </div>

        {msg && <div style={{ fontSize: '0.85rem', color: couleurMessage(niveauMessage(msg)), fontWeight: 600 }}>{msg}</div>}

        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={sending}
            style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
            Annuler
          </button>
          <button onClick={envoyer} disabled={sending || !email.trim() || (mode === 'programmee' && !sessionId) || (mode === 'libre' && !periode.trim())}
            style={{ padding: '0.45rem 1.2rem', borderRadius: '8px', background: '#E87722', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
            {sending ? '⏳ Envoi...' : '📧 Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page principale FormateurDetail ──────────────────────────────────────────

// ── Signature numérisée (apposée sur les attestations de formation) : état, demande, redemande, retrait ──────────────
function BlocSignatureFormateur({ formateurId, formateur, canEdit }) {
  const [etat, setEtat] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [message, setMessage] = useState('');
  const [modalMotif, setModalMotif] = useState(null); // null | { motif }
  const [occupe, setOccupe] = useState(false);

  const charger = React.useCallback(async () => {
    try { setEtat(await apiFetch(`/formateurs/${formateurId}/signature`)); } catch { /* état non bloquant */ }
  }, [formateurId]);
  useEffect(() => { charger(); }, [charger]);

  const utilisable = etat && (etat.etat === 'valide' || etat.etat === 'a_renouveler');
  useEffect(() => {
    if (!canEdit || !utilisable) { setApercu(null); return undefined; }
    let url = null; let annule = false;
    fetch(`${API_BASE}/formateurs/${formateurId}/signature/image`, { headers: { Authorization: `Bearer ${localStorage.getItem('gf_token')}` } })
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b && !annule) { url = URL.createObjectURL(b); setApercu(url); } })
      .catch(() => {});
    return () => { annule = true; if (url) URL.revokeObjectURL(url); };
  }, [canEdit, utilisable, formateurId, etat?.consentement_at]);

  async function demander(motif) {
    setOccupe(true); setMessage('');
    try {
      const r = await apiFetch(`/formateurs/${formateurId}/signature/demander`, { method: 'POST', body: JSON.stringify({ motif: motif || '' }) });
      setMessage(r.smsEnvoye
        ? `✅ Demande envoyée à ${r.email} (lien + QR code) ; le code de sécurité est parti par SMS.`
        : `⚠ Demande envoyée à ${r.email}, mais le SMS du code n'est pas parti (${r.smsErreur}). Communiquez le code affiché ci-dessous au formateur.`);
      setModalMotif(null);
    } catch (e) { setMessage(`❌ ${e.message}`); }
    finally { setOccupe(false); charger(); }
  }
  async function retirer() {
    if (!window.confirm(`Retirer la signature de ${formateur.nom_formateur} ?\nL'image est supprimée ; les prochaines attestations seront à scanner manuellement.`)) return;
    setOccupe(true); setMessage('');
    try { await apiFetch(`/formateurs/${formateurId}/signature`, { method: 'DELETE' }); setMessage('✅ Signature supprimée.'); }
    catch (e) { setMessage(`❌ ${e.message}`); }
    finally { setOccupe(false); charger(); }
  }

  const e = etat || formateur.signature || {};
  const dateFr = d => new Date(d).toLocaleDateString('fr-FR');
  const btn = { fontSize: '0.78rem', padding: '0.25rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', cursor: 'pointer' };
  return (
    <>
      <div className="info-row">
        <label>Signature numérique</label>
        <span><BadgeSignatureFormateur signature={e} avecLibelle /></span>
      </div>
      {utilisable && (
        <div className="info-row">
          <label>Consentement</label>
          <span>{dateFr(e.consentement_at)} · échéance {dateFr(e.echeance)} ({e.jours_restants} j)</span>
        </div>
      )}
      {apercu && (
        <div className="info-row">
          <label>Aperçu</label>
          <span><img src={apercu} alt="Signature du formateur" style={{ maxHeight: 60, maxWidth: 180, background: '#fff', border: '1px solid var(--border)', borderRadius: 4, padding: 4 }} /></span>
        </div>
      )}
      {etat?.demande_en_cours && (
        <div className="info-row">
          <label>Demande en cours</label>
          <span>valable jusqu'au {dateFr(etat.demande_expire_at)}{etat.code && <> · code SMS : <strong style={{ letterSpacing: 2 }}>{etat.code}</strong></>}</span>
        </div>
      )}
      {canEdit && formateur.actif !== false && (
        <div className="info-row" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
          <label />
          <span style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {!utilisable && <button style={btn} disabled={occupe} onClick={() => demander('')}>{etat?.demande_en_cours ? '✉ Renvoyer la demande' : '✍ Demander la signature'}</button>}
            {utilisable && <button style={btn} disabled={occupe} onClick={() => setModalMotif({ motif: '' })}>↻ Redemander (qualité insuffisante)</button>}
            {(utilisable || etat?.demande_en_cours) && <button style={{ ...btn, color: '#dc2626' }} disabled={occupe} onClick={retirer}>🗑 {utilisable ? 'Retirer la signature' : 'Annuler la demande'}</button>}
          </span>
        </div>
      )}
      {message && <div style={{ fontSize: '0.8rem', marginTop: '0.4rem', color: couleurMessage(niveauMessage(message)) }}>{message}</div>}
      {modalMotif && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', width: 480, maxWidth: '95vw' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)' }}>↻ Redemander la signature</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Un nouveau lien (e-mail + QR code) et un nouveau code (SMS) sont envoyés à {formateur.nom_formateur}.
              La signature actuelle reste utilisée jusqu'à ce que la nouvelle soit validée.
            </p>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Motif (repris dans l'e-mail, facultatif)</label>
            <textarea rows={3} value={modalMotif.motif} onChange={ev => setModalMotif({ motif: ev.target.value })}
              placeholder="Ex. : signature trop petite, trait trop fin ou illisible…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.7rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setModalMotif(null)}>Annuler</button>
              <button className="btn-primary" disabled={occupe} onClick={() => demander(modalMotif.motif)}>{occupe ? '⏳ Envoi…' : '✉ Envoyer la demande'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function FormateurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [adjFormateurs, setAdjFormateurs] = useState({ prev: null, next: null });
  const [activeTab, setActiveTab] = useState('prestations');

  useEffect(() => {
    if (!id) return;
    apiFetch(`/formateurs/${id}/adjacent`).then(d => setAdjFormateurs(d)).catch(() => {});
  }, [id]);
  const [modalModif, setModalModif] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [archiving, setArchiving] = useState(false);
  const [modalDispo, setModalDispo] = useState(false);

  const { data, loading } = useApi(`/formateurs/${id}`, {}, [refresh]);
  const { data: formationsData } = useApi('/formations', { limit: 500, actif: true }, []);

  const canEdit = hasRole('utilisateur');
  const formations = formationsData?.data || [];

  if (loading || !data) return <Loading />;

  const isInterne   = data.type_formateur === 'interne';
  const isVacataire = data.type_formateur === 'vacataire';
  const isOf        = data.type_formateur === 'of';

  async function handleArchiver() {
    if (!window.confirm(`Archiver le formateur "${data.nom_formateur}" ?\nIl sera masqué des listes mais pourra être restauré.`)) return;
    setArchiving(true);
    try {
      await apiFetch(`/formateurs/${id}`, { method: 'DELETE' });
      navigate('/formateurs');
    } catch (e) { alert(`Erreur : ${e.message}`); setArchiving(false); }
  }

  async function handleRestaurer() {
    setArchiving(true);
    try {
      await apiFetch(`/formateurs/${id}/restaurer`, { method: 'POST' });
      window.location.reload();
    } catch (e) { alert(`Erreur : ${e.message}`); setArchiving(false); }
  }

  const TABS = [
    { key: 'prestations', label: '📋 Prestations & Formations' },
    ...(!isInterne ? [
      { key: 'documents',   label: '📁 Documents administratifs' },
      { key: 'conventions', label: '📝 Contrat et commandes' },
    ] : []),
  ];

  // Indicateurs documents
  const docs = data.documents || [];
  const expiredCount = docs.filter(d => d.date_fin_validite && new Date(d.date_fin_validite) < new Date()).length;
  const soonCount    = docs.filter(d => {
    if (!d.date_fin_validite) return false;
    const diff = Math.round((new Date(d.date_fin_validite) - new Date()) / 86400000);
    return diff >= 0 && diff <= 60;
  }).length;

  return (
    <div className="page">
      <PageHeader
        title={data.nom_formateur}
        subtitle={[(data.type_formateur === 'of' ? (data.organisme_nom || data.societe) : data.societe), data.formateur_id].filter(Boolean).join(' · ')}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate(`/formateurs/${adjFormateurs.prev}`)}
              disabled={!adjFormateurs.prev} style={{ opacity: adjFormateurs.prev ? 1 : 0.35 }}
              title={adjFormateurs.prev || 'Premier formateur'}>◀ Précédent</button>
            <button className="btn-secondary" onClick={() => navigate(`/formateurs/${adjFormateurs.next}`)}
              disabled={!adjFormateurs.next} style={{ opacity: adjFormateurs.next ? 1 : 0.35 }}
              title={adjFormateurs.next || 'Dernier formateur'}>Suivant ▶</button>
            {canEdit && (
              <>
                <button onClick={() => setModalDispo(true)}
                  style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #E87722', background: 'transparent', color: '#E87722', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                  📧 Demander disponibilité
                </button>
                <button disabled title="Disponible prochainement — application SMS non configurée"
                  style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: '0.85rem', fontWeight: 600, cursor: 'not-allowed', opacity: 0.5 }}>
                  💬 SMS
                </button>
                {data.actif === false && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉ</span>
                )}
                <button className="btn-secondary" onClick={() => setModalModif(true)}>✏️ Modifier</button>
                {data.actif !== false ? (
                  <button onClick={handleArchiver} disabled={archiving}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#f87171', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {archiving ? '⏳...' : '🗑 Archiver'}
                  </button>
                ) : (
                  <button onClick={handleRestaurer} disabled={archiving}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--accent-emerald)', background: 'transparent', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {archiving ? '⏳...' : '↩ Restaurer'}
                  </button>
                )}
              </>
            )}
            <Badge variant={isInterne ? 'badge-neutral' : isOf ? 'badge-warning' : 'badge-info'}>
              {isInterne ? '👤 Interne' : isOf ? '🏛 OF' : '🏢 Vacataire'}
            </Badge>
            <Badge variant={data.actif ? 'badge-success' : 'badge-danger'}>
              {data.actif ? 'Actif' : 'Inactif'}
            </Badge>
          </div>
        }
      />

      {/* Carte infos formateur */}
      <div className="detail-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 className="card-title">Coordonnées</h3>
          <div className="info-grid">
            {data.email    && <div className="info-row"><label>Email</label><span><a href={`mailto:${data.email}`} style={{ color: 'var(--accent-blue)' }}>{data.email}</a></span></div>}
            {data.tel      && <div className="info-row"><label>Téléphone</label><span>{data.tel}</span></div>}
            {data.adresse1 && <div className="info-row"><label>Adresse</label><span>{[data.adresse1, data.adresse2, [data.cp, data.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span></div>}
            {!isInterne && data.siret && <div className="info-row"><label>SIRET</label><span>{data.siret}</span></div>}
            {!isInterne && data.contrat_ref && <div className="info-row"><label>Réf. contrat</label><span>{data.contrat_ref}</span></div>}
            {!isInterne && <div className="info-row"><label>TVA</label><span style={{ color: data.assujetti_tva ? '#16a34a' : 'var(--text-tertiary)', fontWeight: data.assujetti_tva ? 600 : 400 }}>{data.assujetti_tva ? '✓ Assujetti (20 %)' : 'Non assujetti'}</span></div>}
          </div>
        </div>
        <div className="card">
          <h3 className="card-title">Résumé activité</h3>
          <div className="info-grid">
            <div className="info-row"><label>Sessions dispensées</label><span><strong>{data.sessions?.length || 0}</strong></span></div>
            <div className="info-row"><label>Formations habilitées</label><span><strong>{data.formation_liens?.length || 0}</strong></span></div>
            {!isInterne && <div className="info-row"><label>Documents déposés</label><span><strong>{docs.length}</strong></span></div>}
            {expiredCount > 0 && (
              <div className="info-row">
                <label>Documents expirés</label>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>⚠️ {expiredCount}</span>
              </div>
            )}
            {soonCount > 0 && expiredCount === 0 && (
              <div className="info-row">
                <label>Expire bientôt</label>
                <span style={{ color: '#f59e0b', fontWeight: 700 }}>⚠️ {soonCount} document{soonCount > 1 ? 's' : ''}</span>
              </div>
            )}
            <BlocSignatureFormateur formateurId={id} formateur={data} canEdit={canEdit} />
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '0.3rem', borderBottom: '2px solid var(--border)', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        {TABS.map(tab => {
          let badge = null;
          if (tab.key === 'documents' && expiredCount > 0) badge = { count: expiredCount, color: '#ef4444' };
          else if (tab.key === 'documents' && soonCount > 0) badge = { count: soonCount, color: '#f59e0b' };

          return (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.55rem 1rem', borderRadius: '6px 6px 0 0', border: 'none',
                cursor: 'pointer', fontWeight: activeTab === tab.key ? 700 : 500,
                background: activeTab === tab.key ? 'var(--bg-card)' : 'transparent',
                color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
                borderBottom: activeTab === tab.key ? '2px solid #E87722' : '2px solid transparent',
                fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}>
              {tab.label}
              {badge && (
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.45rem',
                  borderRadius: '10px', background: badge.color, color: '#fff',
                }}>
                  {badge.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenu onglet */}
      <div className="card">
        {activeTab === 'prestations' && (
          <OngletPrestations
            formateur={data}
            formations={formations}
            canEdit={canEdit}
            onRefresh={() => setRefresh(r => r + 1)}
          />
        )}
        {activeTab === 'documents' && (
          <OngletDocuments
            formateur={data}
            canEdit={canEdit}
            onRefresh={() => setRefresh(r => r + 1)}
          />
        )}
        {activeTab === 'conventions' && (
          <OngletConventions formateur={data} onRefresh={() => setRefresh(r => r + 1)} />
        )}
      </div>

      {/* Modal modifier */}
      {modalModif && (
        <ModalModifier
          formateur={data}
          onSave={() => { setModalModif(false); setRefresh(r => r + 1); }}
          onClose={() => setModalModif(false)}
        />
      )}

      {/* Modal demande disponibilité */}
      {modalDispo && (
        <ModalDisponibilite
          formateur={data}
          formations={formations}
          onClose={() => setModalDispo(false)}
        />
      )}
    </div>
  );
}
